import React from "react";
import { CustomTextField } from "./CustomTextField";
import { TextFieldProps } from "@mui/material";
import { MuiTextFieldProps } from "@mui/lab/internal/pickers/PureDateInput";

interface DecimalFieldProps {
  name: string;
  label?: string;
  value: number | string | null | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  InputProps?: TextFieldProps["InputProps"];
  sx?: MuiTextFieldProps["sx"];
  max?: number;
  info?: string;
  /** Number of decimal places allowed (default: 2) */
  decimalPlaces?: number;
}

const sanitizeDecimal = (value: string, decimalPlaces: number): string => {
  if (value === "" || value === ".") return value;

  // Allow digits and one decimal point
  const cleaned = value.replace(/[^0-9.]/g, "");

  // Only allow one decimal point
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    return parts[0] + "." + parts.slice(1).join("");
  }

  // Limit decimal places
  if (parts.length === 2 && parts[1].length > decimalPlaces) {
    return parts[0] + "." + parts[1].slice(0, decimalPlaces);
  }

  // Remove leading zeros (but keep "0." and "0")
  if (parts[0].length > 1 && parts[0].startsWith("0") && parts[0][1] !== ".") {
    parts[0] = String(Number(parts[0]));
  }

  return parts.join(".");
};

const toDisplayValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (value === 0 || value === "0") return "";
  return String(value);
};

export const MuiDecimalField: React.FC<DecimalFieldProps> = ({
  name,
  label,
  value,
  onChange,
  error,
  disabled,
  placeholder = "0.00",
  InputProps,
  sx,
  max,
  info,
  decimalPlaces = 2,
}) => {
  const [internalError, setInternalError] = React.useState<string | undefined>();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const sanitized = sanitizeDecimal(raw, decimalPlaces);

    const numericValue = Number(sanitized);

    if (max !== undefined && !isNaN(numericValue) && numericValue > max) {
      setInternalError(`Maximum allowed value is ${max}`);
      return;
    }

    setInternalError(undefined);

    onChange({
      ...e,
      target: {
        ...e.target,
        name,
        value: sanitized,
      },
    });
  };

  React.useEffect(() => {
    setInternalError(undefined);
  }, [value]);

  return (
    <CustomTextField
      type="text"
      inputMode="decimal"
      name={name}
      label={label}
      fullWidth
      value={toDisplayValue(value)}
      placeholder={placeholder}
      error={!!error || !!internalError}
      helperText={error || internalError}
      onChange={handleChange}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      inputProps={{
        inputMode: "decimal",
        pattern: "[0-9]*\\.?[0-9]*",
      }}
      sx={sx}
      InputProps={InputProps}
      info={info}
    />
  );
};
